import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Palmtree, CalendarDays, Gift, Sprout } from 'lucide-react';
import { getLeaveSummary } from '../../../services/dashboardService';
import { Card, CardHeader, CardTitle, EmptyState } from '../styles';
import { formatDate } from '../../../utils/Utils';

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
`;

const Metric = styled.div`
  background: ${({ $bg }) => $bg || '#F8FAFC'};
  border-radius: 8px;
  padding: 10px 12px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.dark?.medium || '#6B7280'};
  text-transform: uppercase;
  letter-spacing: 0.2px;
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${({ $color, theme }) => $color || theme.colors.text.primary};
  margin-top: 2px;
`;

const Section = styled.div`
  margin-top: 4px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RecentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RecentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  background: #F8FAFC;
  border-radius: 6px;
  padding: 8px 10px;
`;

const RecentLeft = styled.div`
  min-width: 0;
  flex: 1;
`;

const RecentName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentDates = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark?.medium || '#6B7280'};
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $tone }) =>
    $tone === 'approved' ? '#DCFCE7' :
    $tone === 'waiting' ? '#FEF3C7' :
    $tone === 'rejected' ? '#FEE2E2' : '#E0E7FF'};
  color: ${({ $tone }) =>
    $tone === 'approved' ? '#166534' :
    $tone === 'waiting' ? '#92400E' :
    $tone === 'rejected' ? '#991B1B' : '#3730A3'};
`;

const EntitlementValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  white-space: nowrap;
`;

export default function LeaveSummaryCard() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    getLeaveSummary().then(res => setData(res.data?.data));
  }, []);

  if (!data) return null;

  // "own" eksenli türler (ör. Babalık İzni) kendi hak ediş/kalanını taşır ama
  // yıllık toplama GİRMEZ — bir kiracıda hiç `annual` eksenli tür olmasa da
  // (yalnız own/none türleri varsa) bu liste dolu olabilir. Eskiden hasData
  // yalnız annual toplamına/son izinlere bakıyordu; own türü olan ama henüz
  // annual hakkı/kullanımı/son izni olmayan bir çalışan kartı BOŞ görürdü.
  const otherEntitlements = data.variations?.filter((v) => v.balanceScope === 'own') ?? [];

  // Aylık birikim / devir tavanı olan türler (bkz. backend Common/LeaveAccrualEngine.cs):
  // "bir sonraki birikim" ve "yıl dönümünde nakde çevrilecek" bilgisi. Yıllık + tavansız
  // türde alanlar boş gelir — kart eskisi gibi kalır.
  const accrualRows = (data.variations ?? []).filter((v) =>
    (v.accrualFrequency === 'monthly' && v.nextAccrualDate) || Number(v.projectedPayout) > 0);

  const hasData = data.totalEntitlement > 0
    || data.totalUsed > 0
    || (data.recentLeaves?.length ?? 0) > 0
    || otherEntitlements.length > 0;

  return (
    <Card $delay="0.15s">
      <CardHeader>
        <CardTitle>{t('dashboard.leaveSummary')}</CardTitle>
      </CardHeader>
      {!hasData ? (
        <EmptyState>
          <Palmtree size={32} />
          {t('dashboard.noLeaveData')}
        </EmptyState>
      ) : (
        <>
          <MetricsGrid>
            <Metric $bg="#EFF6FF">
              <MetricLabel>{t('dashboard.remainingAnnualEntitlement')}</MetricLabel>
              <MetricValue $color="#0291EF" data-testid="remaining-annual-value">
                {/* Kırpma KALKTI: negatif bakiye (aşım) artık gerçek değeriyle
                    gösterilir — Math.max(0, ...) müşterinin şikayetini gizliyordu. */}
                {Number(data.totalEntitlement) - Number(data.totalUsed)}
              </MetricValue>
            </Metric>
            <Metric $bg="#F0FDF4">
              <MetricLabel>{t('dashboard.completedYears')}</MetricLabel>
              <MetricValue $color="#16a34a">{data.completedYears ?? 0}</MetricValue>
            </Metric>
            <Metric $bg="#FFFBEB" style={{ gridColumn: '1 / span 2' }}>
              <MetricLabel>{t('dashboard.nextEntitlementDate')}</MetricLabel>
              <MetricValue $color="#B45309">
                {data.nextEntitlementDate
                  ? formatDate(data.nextEntitlementDate, i18n.language)
                  : '—'}
              </MetricValue>
            </Metric>
          </MetricsGrid>

          <Section>
            <SectionTitle>
              <CalendarDays size={14} />
              {t('dashboard.recentLeaves')}
            </SectionTitle>
            {(data.recentLeaves?.length ?? 0) === 0 ? (
              <RecentRow>
                <RecentLeft>
                  <RecentDates>{t('dashboard.noRecentLeaves')}</RecentDates>
                </RecentLeft>
              </RecentRow>
            ) : (
              <RecentList>
                {data.recentLeaves.map((l) => (
                  <RecentRow key={l.id}>
                    <RecentLeft>
                      <RecentName>{l.leaveVariationName}</RecentName>
                      <RecentDates>
                        {formatDate(l.leaveStart, i18n.language)} → {formatDate(l.leaveEnd, i18n.language)}
                      </RecentDates>
                    </RecentLeft>
                    <StatusBadge $tone={l.status}>
                      {t(`dashboard.leaveStatus.${l.status}`, l.status)}
                    </StatusBadge>
                  </RecentRow>
                ))}
              </RecentList>
            )}
          </Section>

          {accrualRows.length > 0 && (
            <Section>
              <SectionTitle>
                <Sprout size={14} />
                {t('dashboard.accrualInfo')}
              </SectionTitle>
              <RecentList>
                {accrualRows.map((v) => {
                  const key = v.leaveVariationId || v.name;
                  const unit = (v.isHourly ? t('dashboard.unitHour') : t('dashboard.unitDay')).toLowerCase();
                  return (
                    <RecentRow key={key} data-testid="accrual-info-row">
                      <RecentLeft>
                        <RecentName>{v.name}</RecentName>
                        {v.accrualFrequency === 'monthly' && v.nextAccrualDate && (
                          <RecentDates data-testid={`next-accrual-${key}`}>
                            {t('dashboard.nextAccrual', {
                              date: formatDate(v.nextAccrualDate, i18n.language),
                              days: Number(v.nextAccrualDays ?? 0),
                              unit,
                            })}
                          </RecentDates>
                        )}
                        {Number(v.projectedPayout) > 0 && v.nextAnniversary && (
                          <RecentDates data-testid={`projected-payout-${key}`}>
                            {t('dashboard.projectedPayout', {
                              date: formatDate(v.nextAnniversary, i18n.language),
                              days: Number(v.projectedPayout),
                              cap: Number(v.carryOverCap ?? 0),
                              unit,
                            })}
                          </RecentDates>
                        )}
                      </RecentLeft>
                    </RecentRow>
                  );
                })}
              </RecentList>
            </Section>
          )}

          {otherEntitlements.length > 0 && (
            <Section>
              <SectionTitle>
                <Gift size={14} />
                {t('dashboard.otherEntitlements')}
              </SectionTitle>
              <RecentList>
                {otherEntitlements.map((v) => {
                  const key = v.leaveVariationId || v.name;
                  // Bulgu F: saatlik bir own tür (ör. Süt İzni) gün esaslı
                  // türlerin yanında ETİKETSİZ bir sayı olarak duruyordu.
                  const unit = v.isHourly ? t('dashboard.unitHour') : t('dashboard.unitDay');

                  // Bulgu A: olay başına hak eden türde (babalık/evlilik/ölüm/
                  // doğum) YILLIK hakediş kuralı yoktur — backend entitlement=0
                  // döner ve `entitlement − used` kullanım kadar NEGATİFE
                  // düşüyordu ("Babalık İzni −5/0"). Bu türlerde anlamlı tavan
                  // olay başına instanceLimit'tir; "kalan" hiç gösterilmez.
                  if (v.entitlementBasis === 'perEvent') {
                    return (
                      <RecentRow key={key} data-testid="other-entitlement-row">
                        <RecentLeft>
                          <RecentName>{v.name}</RecentName>
                          <RecentDates data-testid={`per-event-used-${key}`}>
                            {t('dashboard.perEventUsed', { used: Number(v.used ?? 0), unit })}
                          </RecentDates>
                        </RecentLeft>
                        <EntitlementValue data-testid={`per-event-limit-${key}`}>
                          {v.instanceLimit === null || v.instanceLimit === undefined
                            ? t('dashboard.perEventNoLimit')
                            : t('dashboard.perEventLimit', { limit: Number(v.instanceLimit), unit })}
                        </EntitlementValue>
                      </RecentRow>
                    );
                  }

                  const remaining = Number(v.entitlement) - Number(v.used) - Number(v.pending || 0);
                  return (
                    <RecentRow key={key} data-testid="other-entitlement-row">
                      <RecentLeft>
                        <RecentName>{v.name}</RecentName>
                      </RecentLeft>
                      <EntitlementValue data-testid={`own-remaining-${key}`}>
                        {remaining}/{Number(v.entitlement ?? 0)} {unit.toLowerCase()}
                      </EntitlementValue>
                    </RecentRow>
                  );
                })}
              </RecentList>
            </Section>
          )}
        </>
      )}
    </Card>
  );
}
